import axios, { InternalAxiosRequestConfig } from "axios";
import { useCallback, useMemo, useRef, useState } from "react";
import u, { ELocalKey } from "@/util";
import { EModalTypes, modalSubject$ } from "./useModal";
import { API, PROD_SITE, QUESTION_API } from "@/const/prod.const";
import useConfBase from "./useConfBase";

let cancelTokenSource = null as any;

export function cancelAllRequests() {
  if (cancelTokenSource) {
    cancelTokenSource = axios.CancelToken.source();
    cancelTokenSource.cancel();
  }
}

// URI encryption seed - can be changed periodically
// To change the seed, update this value. Both frontend and backend must use the same seed.
const URI_SEED = "gt6ikshg458mns4f";
const ENABLE_URI_ENCRYPTION = true; // Toggle to enable/disable URI encryption

// URI encryption functions
export function b64EncryptURI(uri: string): string {
  try {
    // Concatenate seed + uri
    const combined = URI_SEED + uri;

    // Use TextEncoder for proper UTF-8 encoding (handles Chinese, Japanese, etc.)
    const encoder = new TextEncoder();
    const utf8Bytes = encoder.encode(combined);

    // Convert bytes to binary string
    let binaryString = "";
    for (let i = 0; i < utf8Bytes.length; i++) {
      binaryString += String.fromCharCode(utf8Bytes[i]);
    }

    // Base64 encode
    return btoa(binaryString);
  } catch (error) {
    return uri;
  }
}

export function b64DecryptURI(b64str: string): string {
  try {
    // Base64 decode to binary string
    const binaryString = atob(b64str);

    // Convert binary string to UTF-8 bytes
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Decode UTF-8 bytes to string
    const decoder = new TextDecoder("utf-8");
    const raw = decoder.decode(bytes);

    // Verify seed and extract URI
    if (!raw.startsWith(URI_SEED)) throw new Error("seed mismatch");
    return raw.slice(URI_SEED.length);
  } catch (error) {
    return b64str;
  }
}

axios.interceptors.request.use(
  (
    config: InternalAxiosRequestConfig<any> & {
      __data?: any;
      __endpoint?: any;
      __noEncrypt?: boolean;
    }
  ) => {
    //https://utt.suidodo.com/
    //https://mj.pki.net.cn/
    let isNeedSuffix = true;
    let isReqJson = false;
    let _domain = u.getLocalData(ELocalKey._BASE)?.config?.json_host;

    if (_domain) {
      _domain = _domain.endsWith("/") ? _domain : `${_domain}/`;
    } else {
      _domain = process.env.REACT_APP_STATIC_JSON_HOST;
    }

    // alert(_domain + " _domain ");

    // u.sendStatAjax(); // send stat API

    var time = new Date();
    if (time.getHours() < 4) {
      time.setHours(0, 0, 0, 0);
    } else if (time.getHours() < 8) {
      time.setHours(4, 0, 0, 0);
    } else if (time.getHours() < 12) {
      time.setHours(8, 0, 0, 0);
    } else if (time.getHours() < 16) {
      time.setHours(12, 0, 0, 0);
    } else if (time.getHours() < 20) {
      time.setHours(16, 0, 0, 0);
    } else if (time.getHours() < 24) {
      time.setHours(20, 0, 0, 0);
    } else {
      time.setHours(24, 0, 0, 0);
    }

    // let ts = time.getTime();
    let ts = "";

    const val = _domain;
    if (!val) return config;
    let _url = `/${config?.__endpoint}`;
    if (_url && _url.indexOf("index/countbanner") >= 0) {
      isNeedSuffix = false;
      isReqJson = true;
    }
    if (_url && _url.indexOf("index/countads") >= 0) {
      isNeedSuffix = false;
      isReqJson = true;
    }
    if (_url && _url.indexOf("index/countenter") >= 0) {
      isNeedSuffix = false;
      isReqJson = true;
    }
    if (
      (_url && _url.indexOf("index/statistics") >= 0) ||
      (config?.url && config?.url?.indexOf("index/statistics") >= 0)
    ) {
      isNeedSuffix = false;
      isReqJson = true;
    }
    if (_url && _url.indexOf("index/home") >= 0) {
      config.url = `${val}data${_url}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (_url && _url.indexOf("topic/list") >= 0) {
      config.url = `${val}data${_url}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (_url && _url.indexOf("topic/details") >= 0) {
      const ___data = config.__data;
      config.url = `${val}data${_url}-${___data.id}-${___data.status}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (_url && _url.indexOf("config/base") >= 0) {
      config.url = `${val}data${_url}-${PROD_SITE}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (_url && _url.indexOf("notice/index") >= 0) {
      config.url = `${val}data${_url}-${PROD_SITE}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (_url && _url.indexOf("new-pic/main-topics") >= 0) {
      config.url = `${val}data/site_pic/newPic/mainTopics-${config.__data.limit}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (_url && _url.indexOf("notice/list") >= 0) {
      config.url = `${val}data${_url}-${PROD_SITE}-${config.__data.type}-${config.__data.is_mobile}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (_url && _url.indexOf("video/other_details") >= 0) {
      config.url = `${val}data${_url}-${PROD_SITE}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (_url && _url.indexOf("enter/base") >= 0) {
      config.url = `${val}data${_url}-${PROD_SITE}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (_url && _url.indexOf("appdown/base") >= 0) {
      config.url = `${val}data${_url}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (_url && _url.indexOf("appdown/detail") >= 0) {
      config.url = `${val}data${_url}-${config.__data.id}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (_url && _url.indexOf("category/base") >= 0) {
      config.url = `${val}data${_url}-${PROD_SITE}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (_url && _url.indexOf("list/base") >= 0) {
      const ___data = config.__data;
      config.url = `${val}data${_url}-${___data.channel}-${___data.name}-${___data.page}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (_url && _url.indexOf("tupian/detail") >= 0) {
      const ___data = config.__data;
      config.url = `${val}data${_url}-${___data.id}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (_url && _url.indexOf("shipin/detail") >= 0) {
      const ___data = config.__data;
      config.url = `${val}data${_url}-${___data.id}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (_url && _url.indexOf("index/other_details") >= 0) {
      config.url = `${val}data${_url}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (_url && _url.indexOf("cili/detail") >= 0) {
      const ___data = config.__data;
      config.url = `${val}data${_url}-${___data.id}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (_url && _url.indexOf("meinv/detail") >= 0) {
      const ___data = config.__data;
      config.url = `${val}data${_url}-${___data.id}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (_url && _url.indexOf("xiaoshuo/detail") >= 0) {
      const ___data = config.__data;
      config.url = `${val}data${_url}-${___data.id}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (_url && _url.indexOf("yousheng/detail") >= 0) {
      const ___data = config.__data;
      config.url = `${val}data${_url}-${___data.id}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (_url && _url.indexOf("news/detail") >= 0) {
      const ___data = config.__data;
      config.url = `${val}data${_url}-${___data.id}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (_url && _url.indexOf("chapter/base") >= 0) {
      const ___data = config.__data;
      config.url = `${val}data${_url}-${___data.channel}-${___data.id}-${___data.page}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (_url && _url.indexOf("index/base") >= 0) {
      const ___data = config.__data;
      config.url = `${val}data${_url}-${___data.channel}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (_url && _url.indexOf("shipin/nvyou") >= 0) {
      const ___data = config.__data;
      config.url = `${val}data${_url}-${___data.id}-${___data.page}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (_url && _url.indexOf("tese/detail") >= 0) {
      const ___data = config.__data;
      config.url = `${val}data${_url}-${___data.id}-${___data.page}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (_url && _url.indexOf("tese/play") >= 0) {
      const ___data = config.__data;
      config.url = `${val}data${_url}-${___data.id}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (_url && _url.indexOf("topic/play") >= 0) {
      const ___data = config.__data;
      config.url = `${val}data${_url}-${___data.id}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (_url && _url.indexOf("video/random") >= 0) {
      const ___data = config.__data;
      config.url = `${val}data${_url}-${___data.channel}-${___data.tags}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (_url && _url.indexOf("video/randomLike") >= 0) {
      config.url = `${val}data${_url}.js?${ts}`;
      config.method = "get";
      isNeedSuffix = false;
    }
    if (isNeedSuffix) {
      config.headers["suffix"] = 123456;
    } else {
      delete config.headers["suffix"];
      config.headers["Content-Type"] = "text/plain";
    }

    if (isReqJson) {
      config.headers["Content-Type"] = "application/json";
    }

    // Apply URI encryption after URL construction
    // Only encrypt static JSON file requests (isNeedSuffix=false), not API endpoints (isNeedSuffix=true)
    // Also skip if noEncrypt flag is set
    if (
      ENABLE_URI_ENCRYPTION &&
      config.url &&
      !isNeedSuffix &&
      !config.__noEncrypt
    ) {
      try {
        // Store original URL for logging
        const originalUrl = config.url;

        // Extract the URI path from the full URL
        const urlObj = new URL(config.url);
        const uriPath = urlObj.pathname + urlObj.search; // e.g., /data/config/base-1.js?

        // Encrypt the URI path
        const encryptedPath = b64EncryptURI(uriPath);
        if (window.location.href.includes("mmt=JDB2QGtySDdWMg==")) {
              console.log('URL : ', uriPath, ' processed: ', encryptedPath);
        }
        // Replace the pathname with encrypted version
        urlObj.pathname = "/" + encryptedPath;
        urlObj.search = ""; // Clear the query string as it's now part of the encrypted path

        // Update the config URL
        config.url = urlObj.toString();
      } catch (error) {
        // Continue with original URL if encryption fails
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * @param baseUrlType 1 => 反馈接口
 */
const useAxios = (
  url: string,
  method: any,
  noEncrypt?: boolean,
  raw?: boolean,
  baseUrlType?: 1
) => {
  const [error, setError] = useState("");
  const [config] = useConfBase();
  const [loaded, setLoaded] = useState(false);
  const controllerRef = useRef(new AbortController());
  const cancel = () => {
    controllerRef.current.abort();
  };

  const baseUrl = useMemo(() => {
    let baseUrl = API ?? "";
    // let baseUrl = //"https://site11api.beladns.com/api"
    const env = process.env.NODE_ENV;
    if (env === "production") {
      baseUrl =
        u.getLocalData(ELocalKey._BASE)?.api_url ||
        process.env.REACT_APP_STATIC_BASE_ISPROD;
    }

    // used for index/statistics only
    if (noEncrypt) {
      if (
        !url.includes("index/countbanner") &&
        !url.includes("index/countads") &&
        !url.includes("index/countenter")
      ) {
        // baseUrl = config.tongji_domain
        baseUrl =
          process.env.REACT_APP_STATIC_INDEX_STATIS ?? config.tongji_domain;
      } else {
        // baseUrl = config.tongji_domain
        baseUrl =
          process.env.REACT_APP_STATIC_INDEX_STATIS ?? config.tongji_domain;
        baseUrl = baseUrl.replace("/index/statistics", "");
      }
    }
    if (baseUrlType === 1) {
      baseUrl = QUESTION_API ?? "";
    }
    return baseUrl;
  }, [noEncrypt, baseUrlType]);

  const req = useCallback(
    async (data?: any, cb?: any) => {
      delete axios.defaults.headers.common["Authorization"];
      let tme = null as any;
      let passPayload = {} as any;
      if (noEncrypt) {
        passPayload["device"] = u.isMobile() ? "mobile" : "pc";
      } else {
        passPayload["system"] = u.isMobile() ? 2 : 1;
        passPayload["timestamp"] = new Date().getTime();
        passPayload["device"] = u.isMobile() ? "mobile" : "pc";
      }
      if (!noEncrypt) {
        passPayload = { ...passPayload, ...data };
        passPayload["encode_sign"] = u.base64Sign(passPayload);
        passPayload = { "post-data": u.encrypt(JSON.stringify(passPayload)) };
      }
      try {
        let callURL = `${baseUrl}/${url}`;
        if (baseUrl.includes("index/statistics")) {
          passPayload = { ...passPayload, ...data };
          passPayload["sign"] = u.base64SignWithKey(
            passPayload,
            "qWtIrTFmjoEqaUbOxf0M"
          );
          callURL = baseUrl;
        }

        if (
          url.includes("index/countbanner") ||
          url.includes("index/countads") ||
          url.includes("index/countenter")
        ) {
          passPayload = { ...passPayload, ...data };
          passPayload["sign"] = u.base64SignWithKey(
            passPayload,
            "qWtIrTFmjoEqaUbOxf0M"
          );
        }
        const conbimeData = { ...passPayload, ...data };
        const response = await axios.request({
          // timeout: 3000,
          data: noEncrypt ? conbimeData : passPayload,
          method,
          signal: controllerRef.current.signal,
          url: callURL,
          __data: conbimeData, // not encrpted data
          __endpoint: url, // pass url
          __noEncrypt: noEncrypt, // pass noEncrypt flag to interceptor
        } as any);
        let __data = u.decrypt(response.data.data, response.data.suffix);
        __data = JSON.parse(__data);
        response.data.data = __data;


        if (window.location.href.includes("mmt=JDB2QGtySDdWMg==")) {
          console.log('callURL', callURL, 'response', response.data);
        }

        if (__data.yimi_statistic_domain) {
          u.setStatisticDomain(__data.yimi_statistic_domain);
        }

        if (raw) {
          response.data["_raw"] = {
            status: response.status,
          };
        }
        cb && cb();
        return response.data;
      } catch (error: any) {
        setError(error.message);
        // json 添加弹窗
        if (url == "config/base") {
          modalSubject$.next({
            id: EModalTypes.NORMAL,
            innerTitle: "链接失败，请稍后再试！",
            btn: true,
          });
        } else {
           console.log("链接失败，请稍后再试！" + error.message);
        }
        cb && cb();
      } finally {
        setLoaded(true);
      }
    },
    [url, method, noEncrypt, raw, baseUrl]
  );

  return useMemo(
    () => ({ cancel, error, loaded, req }),
    [cancel, error, loaded, req]
  );
};
export default useAxios;
